// 敵基底クラス：HP/向き/AI状態を持つ
import Phaser from 'phaser';
import { ENEMY_SPECS, EnemyKind } from '../data/EnemyTypes';
import { Dir4, vectorToDir } from '../data/Directions';

const KNOCKBACK_DURATION_MS = 140;

export class Enemy extends Phaser.Physics.Arcade.Sprite {
  public kind: EnemyKind;
  public hp: number;
  public facing: Dir4 = 'down';
  // ノックバック中は AI 更新をスキップ
  private knockbackMs = 0;
  // 攻撃クールダウン
  protected attackCooldownMs = 0;

  constructor(scene: Phaser.Scene, x: number, y: number, kind: EnemyKind) {
    const spec = ENEMY_SPECS[kind];
    super(scene, x, y, spec.textureKey, 0);
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.kind = kind;
    this.hp = spec.maxHp;
    this.setScale(spec.scale);
    // 物理ボディ：表示より少し小さく
    const body = this.body as Phaser.Physics.Arcade.Body;
    const w = Math.floor(spec.frameWidth * 0.6);
    const h = Math.floor(spec.frameHeight * 0.55);
    body.setSize(w, h).setOffset((spec.frameWidth - w) / 2, spec.frameHeight - h - 2);
    body.setCollideWorldBounds(true);
  }

  spec() {
    return ENEMY_SPECS[this.kind];
  }

  // 毎フレーム呼ばれる：AI と表示を更新する
  // playerCenter は WorldScene 側から渡す
  override preUpdate(time: number, delta: number): void {
    super.preUpdate(time, delta);
    this.attackCooldownMs = Math.max(0, this.attackCooldownMs - delta);
    if (this.knockbackMs > 0) {
      this.knockbackMs -= delta;
      // 物理だけ動かす、AI は次フレームから復帰
      return;
    }
  }

  // WorldScene から呼ばれる AI 更新（基底はプレイヤーへ近づく）
  tick(playerX: number, playerY: number, delta: number): void {
    if (this.knockbackMs > 0 || !this.active) return;
    const s = this.spec();
    const dist = Phaser.Math.Distance.Between(this.x, this.y, playerX, playerY);
    if (dist > s.detectRange) {
      this.setVelocity(0, 0);
      return;
    }
    // 攻撃範囲内なら停止＋攻撃クールダウン消化
    if (dist <= s.attackRange) {
      this.setVelocity(0, 0);
      // 攻撃の実体（弓矢発射など）は派生クラスでオーバーライド
      this.tryAttack(playerX, playerY);
      void delta;
      return;
    }
    // 追跡：プレイヤー方向へ移動
    const angle = Phaser.Math.Angle.Between(this.x, this.y, playerX, playerY);
    this.setVelocity(Math.cos(angle) * s.speed, Math.sin(angle) * s.speed);
    this.facing = vectorToDir(Math.cos(angle), Math.sin(angle), this.facing);
    this.playWalkAnim();
  }

  // 派生クラスで上書き
  protected tryAttack(_px: number, _py: number): void {
    // 雑魚の接触ダメージは WorldScene 側で overlap で処理する
  }

  // 派生クラスで上書き（歩行アニメ再生）
  protected playWalkAnim(): void {
    // 既定実装：1キャラ1フレームの素材ならアニメ無し
  }

  // ダメージ＋ノックバック
  damage(amount: number, knockbackVx: number, knockbackVy: number): boolean {
    this.hp -= amount;
    this.setTint(0xffffff);
    this.scene.time.delayedCall(60, () => {
      if (this.active) this.clearTint();
    });
    if (knockbackVx !== 0 || knockbackVy !== 0) {
      this.knockbackMs = KNOCKBACK_DURATION_MS;
      this.setVelocity(knockbackVx, knockbackVy);
    }
    if (this.hp <= 0) {
      this.disableBody(true, true);
      return true;
    }
    return false;
  }
}
